import { cacheKey, carryOversAvailable as countCarryOvers, findCached, indexActivePallets, type ActivePalletDto } from '~/utils/palletCache'
import { defineStore } from 'pinia'
import { useApiConfig } from '@/config/api'
import {
  LookupAnswerError,
  LookupNotFoundError,
  SharedPalletError,
  readCandidates,
  mapTruckLoad,
  mapTruckLoads,
  partnersWithTrip,
  mapPallet,
  mapShippedPallets,
  resolveLocation,
  resolveTrip,
  type LoadedPalletInfo,
  type LocationInfo,
  type OrderRowInfo,
  type PalletInfo,
  type TripInfo,
  type TruckLoadInfo,
} from '~/utils/lookupMapping'
import { mapTrailerLayout, type TrailerLayoutDto, type TrailerLayoutInfo } from '~/utils/trailerLayout'
import { mapContainerWork, type ContainerPalletsDto, type ContainerWork } from '~/utils/containerWork'
import { LookupUnreachableError, reach } from '~/utils/lookupReach'
import type { PalletFile } from '~/utils/palletFiles'
import { useWarehouse } from '~/composables/useWarehouse'
import { mapGangEntries, type GangEntry } from '~/utils/warehouses'

export type { LoadedPalletInfo, LocationInfo, OrderRowInfo, PalletInfo, TripInfo, TruckLoadInfo }

// The lookup's shape and the function that runs one live in utils/lookupRun,
// free of the store so they can be tested; they are handed on from here
// because this is where every screen already imports them from.
export { idleLookup, runLookup, type Lookup, type LookupOptions } from '~/utils/lookupRun'

/**
 * The location list as last read, kept on the tablet. Locations are scanned
 * out of range too (the destination of a move, from inside a container), and
 * the list is what turns a scanned name into the id a move is addressed by.
 * A reference list, not anyone's work: it is not cleared at sign-in.
 */
const KEPT_LOCATIONS = 'dockautomation.locations'

function keepLocations(list: Array<Record<string, unknown>>): void {
  try {
    localStorage.setItem(KEPT_LOCATIONS, JSON.stringify(list))
  } catch {
    // No room or no storage: the list is still in memory for this page.
  }
}

function keptLocations(): Array<Record<string, unknown>> | null {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(KEPT_LOCATIONS) ?? 'null')
    return Array.isArray(parsed) && parsed.length > 0 ? parsed as Array<Record<string, unknown>> : null
  } catch {
    return null
  }
}

/**
 * REAL reads for the workflow screens (ID28) — pallet, location, and trip come
 * from the API now; only the confirm submissions still ride the fake sync
 * transport (see fakeBackend.ts). These are the DIRECT path: the operator is
 * waiting on the answer, so they fetch and fail loudly, never queue. The
 * auth-fetch plugin attaches the bearer token to every API call.
 */
export const useLookupStore = defineStore('lookups', {
  state: () => ({
    // Locations are a small reference set fetched once and matched by name
    // client-side — the API's direct lookup needs a warehouse code the barcode
    // does not carry.
    locationCache: null as Array<Record<string, unknown>> | null,

    /**
     * The load-time pallet cache (decision 0013): every pallet still in
     * inventory for the order's products, so a scan is judged from memory and
     * the gun beeps the instant the code lands. Keyed by partner + products;
     * primed when a trip resolves, refreshed on the Load screen's timer,
     * dropped when the screen is left.
     */
    palletCache: null as {
      key: string
      partnerId: number
      productNumbers: string[]
      index: Map<string, PalletInfo>
      fetchedAt: number
    } | null,

    /**
     * Where the cache stands, for the Load screen's gate (decision 0016): no
     * pallet is judged until the floor has been read. `failed` is a first
     * read that did not land; a refresh that fails keeps the rows and the
     * status it had.
     */
    palletCacheStatus: 'idle' as 'idle' | 'loading' | 'ready' | 'failed',

    /**
     * The container a driver is working in on Move Pallets, and its partner's
     * pallets in every container (utils/containerWork.ts, Mark 2026-10-15).
     * Read at the container's door, with a signal; null on the floor.
     */
    containerWork: null as ContainerWork | null,
  }),

  actions: {
    /**
     * One pallet, by its barcode; with `partnerId`, that partner's pallet and no other's.
     *
     * A barcode two partners both have on hand is a question, not a failure: the
     * server answers 409 with the pallets it could mean (task 321), thrown as a
     * SharedPalletError for the screen to ask which (task 2221). Any other no is
     * thrown in the server's own words.
     */
    async fetchPallet(palletId: string, partnerId?: number | null): Promise<PalletInfo> {
      const apiConfig = useApiConfig()
      const whose = typeof partnerId === 'number' ? `?partnerId=${partnerId}` : ''
      // `reach`, not `fetch`: no answer is told apart from a no (lookupReach.ts).
      const response = await reach(
        `${apiConfig.baseURL}/Pallet/${encodeURIComponent(palletId)}${whose}`,
        { headers: { Accept: 'application/json' } },
      )
      if (response.status === 404) {
        const message = await readErrorMessage(response)
        throw new LookupNotFoundError(message ?? `Pallet ${palletId} is not in inventory`)
      }
      if (response.status === 409) {
        const body = await readJson(response)
        const candidates = readCandidates(body)
        const message = problemText(body) ?? `More than one partner holds a pallet with the identifier '${palletId}'.`
        if (candidates.length > 0) throw new SharedPalletError(message, palletId, candidates)
        throw new LookupAnswerError(message)
      }
      if (!response.ok) {
        throw new LookupAnswerError(problemText(await readJson(response)) ?? `Pallet lookup failed (${response.status})`)
      }
      return mapPallet(await response.json())
    },

    /**
     * GET /api/pallets/active — the cache's feed. A read; fails loudly. Given longer than
     * other reads: a banana order's products can be fifteen hundred pallets.
     */
    async fetchActivePallets(partnerId: number, productNumbers: readonly string[]): Promise<ActivePalletDto[]> {
      const apiConfig = useApiConfig()
      const products = encodeURIComponent(productNumbers.map(p => p.trim()).filter(p => p !== '').join(','))
      const response = await reach(
        `${apiConfig.baseURL}/pallets/active?partnerId=${partnerId}&productNumbers=${products}`,
        { headers: { Accept: 'application/json' }, cache: 'no-store' },
        { timeoutMs: 30_000 },
      )
      if (!response.ok) throw new Error(`Active pallets failed (${response.status})`)
      const body = await response.json()
      return Array.isArray(body) ? body as ActivePalletDto[] : []
    },

    /**
     * Have the cache ready for this order's products. A trip whose order
     * wants the same products as the last one keeps what it has; anything
     * else fetches afresh. Never throws: a cache that could not be filled is
     * simply absent, and every scan then takes the round trip as before.
     */
    async primePalletCache(partnerId: number, productNumbers: readonly string[]): Promise<void> {
      const key = cacheKey(partnerId, productNumbers)
      if (this.palletCache?.key === key) return
      this.palletCache = null
      this.palletCacheStatus = 'loading'
      await this.refreshPalletCache(partnerId, [...productNumbers])
    },

    /** Re-read the cache's feed. Keeps the old rows if the read fails. */
    async refreshPalletCache(partnerId?: number, productNumbers?: string[]): Promise<void> {
      const target = partnerId !== undefined && productNumbers !== undefined
        ? { partnerId, productNumbers }
        : this.palletCache
      if (!target) return
      try {
        const rows = await this.fetchActivePallets(target.partnerId, target.productNumbers)
        this.palletCache = {
          key: cacheKey(target.partnerId, target.productNumbers),
          partnerId: target.partnerId,
          productNumbers: [...target.productNumbers],
          index: indexActivePallets(rows),
          fetchedAt: Date.now(),
        }
        this.palletCacheStatus = 'ready'
      } catch {
        // Stale beats absent: the last good list still answers scans, and the
        // background confirm on every hit is what catches what changed. With
        // no list at all, the screen has to know it is blind.
        if (!this.palletCache) this.palletCacheStatus = 'failed'
      }
    },

    /** The cached pallet for a scanned code, or null — synchronous, so the gun can answer at once. */
    cachedPallet(scanned: string): PalletInfo | null {
      return findCached(this.palletCache?.index ?? null, scanned)
    },

    dropPalletCache(): void {
      this.palletCache = null
      this.palletCacheStatus = 'idle'
    },

    /** Loadable carry-over pallets of a product still on the floor, per the cache. */
    carryOversAvailable(productNumber: string, excluding: readonly string[] = []): number {
      return countCarryOvers(this.palletCache?.index ?? null, productNumber, excluding)
    },

    /**
     * Open a container for work: read its partner's pallets in every container
     * (`GET /api/pallets/in-partner-containers`). A read, so it needs a signal
     * and throws as any lookup does: no answer as LookupUnreachableError, a
     * container we do not have as LookupNotFoundError. Nothing changes until it
     * has answered.
     */
    async openContainer(equipmentNumber: string): Promise<ContainerWork> {
      const apiConfig = useApiConfig()
      const response = await reach(
        `${apiConfig.baseURL}/pallets/in-partner-containers?equipmentNumber=${encodeURIComponent(equipmentNumber.trim())}`,
        { headers: { Accept: 'application/json' }, cache: 'no-store' },
        { timeoutMs: 20_000 },
      )
      if (response.status === 404) {
        throw new LookupNotFoundError(`Container ${equipmentNumber.trim().toUpperCase()} is not one we have. Check the number.`)
      }
      if (!response.ok) {
        throw new LookupAnswerError(problemText(await readJson(response)) ?? `The container could not be read (${response.status})`)
      }
      const work = mapContainerWork(await response.json() as ContainerPalletsDto)
      this.containerWork = work
      return work
    },

    /** Done in the container: back on the floor, where a move needs a signal. */
    leaveContainer(): void {
      this.containerWork = null
    },

    async fetchLocation(locationId: string): Promise<LocationInfo> {
      // The tablet's warehouse settles a name that two warehouses both use.
      const warehouseId = useWarehouse().warehouse.value?.id ?? null
      const all = await this.ensureLocations()
      try {
        return resolveLocation(all, locationId, warehouseId)
      } catch (error) {
        // A miss may just mean the cache predates a newly imported location —
        // refresh once before giving up.
        if (error instanceof LookupNotFoundError && this.locationCache) {
          const fresh = await this.ensureLocations(true)
          return resolveLocation(fresh, locationId, warehouseId)
        }
        throw error
      }
    },

    /**
     * Whether the location list on the tablet holds this name, in any
     * warehouse; null while there is no list. Synchronous, for the scan
     * interpreter (utils/workflowScan.ts, KnowsLocation).
     */
    knowsLocationName(name: string): boolean | null {
      if (!this.locationCache) return null
      const wanted = name.trim().toUpperCase()
      return this.locationCache.some(l => String(l.name ?? '').trim().toUpperCase() === wanted)
    },

    async ensureLocations(force = false): Promise<Array<Record<string, unknown>>> {
      if (!force && this.locationCache) return this.locationCache
      const apiConfig = useApiConfig()
      let response: Response
      try {
        response = await reach(`${apiConfig.baseURL}/InventoryLocation`, {
          headers: { Accept: 'application/json' },
        })
      } catch (error) {
        // Out of range with nothing in memory (a reload): the copy kept on the
        // tablet answers. Never for a FORCED read — that is asked for because
        // the list in hand did not have the location, and the same list again
        // would only repeat the miss; no answer is the honest result there.
        if (!force && error instanceof LookupUnreachableError) {
          const kept = keptLocations()
          if (kept) {
            this.locationCache = kept
            return kept
          }
        }
        throw error
      }
      if (!response.ok) throw new Error(`Location list failed (${response.status})`)
      this.locationCache = await response.json()
      keepLocations(this.locationCache!)
      return this.locationCache!
    },

    /**
     * The trucks to load, or to unload: the tablet's own read (`GET /api/truck-loads`).
     *
     * One request. The server puts the trucks together - orders in loading sequence, pallets
     * aboard, whose turn it is - so the list is on screen whole, with its counts, at once.
     * Not narrowed by warehouse or gang here: the gang buttons count every gang's trucks, and
     * the warehouse is a filter the screen applies. A READ, so it takes the direct path and
     * fails loudly rather than queueing.
     *
     * With a time limit, and never from or through the browser's cache (found on the tablet,
     * 2026-10-12). A request left on a connection that died - the tablet rolled out of range,
     * a cable link dropped - never ends by itself, and the browser makes every later request
     * for the same address wait behind it: the list sat on "Loading" for good while every
     * other read answered. `reach` gives up after its limit, and `no-store` means no request
     * ever waits on another.
     */
    /**
     * Each partner's pallet snapshot and how fresh its holds are (`GET /api/pallet-files`, Mark
     * 2026-10-22), for the "Holds may be out of date" band on Load Truck and Move Pallets.
     */
    async fetchPalletFiles(): Promise<PalletFile[]> {
      const apiConfig = useApiConfig()
      const response = await reach(`${apiConfig.baseURL}/pallet-files`, { headers: { Accept: 'application/json' }, cache: 'no-store' })
      if (!response.ok) throw new Error(`Pallet files failed (${response.status})`)
      return (await response.json()) as PalletFile[]
    },

    async fetchTruckLoads(direction: 'load' | 'unload'): Promise<OrderRowInfo[]> {
      const apiConfig = useApiConfig()
      const response = await reach(
        `${apiConfig.baseURL}/truck-loads?direction=${direction}`,
        { headers: { Accept: 'application/json' }, cache: 'no-store' },
      )
      if (!response.ok) throw new Error(`Trucks failed (${response.status})`)
      return mapTruckLoads(await response.json(), direction)
    },

    /**
     * One truck in full, for the Load Truck screen (`GET /api/truck-loads/{partner}/{trip}`):
     * its orders in sequence, what each asks for, and the pallets aboard each.
     *
     * The list hands the partner over with the trip. A trip number scanned bare does not say
     * whose it is, and a trip number is only a partner's own, so the trucks at a door are
     * asked which of them carries it; two partners with the same number is a question for a
     * person, answered by picking the truck from the list.
     */
    async fetchTruck(tripNumber: string, partnerId: number | null): Promise<TruckLoadInfo> {
      const apiConfig = useApiConfig()
      const trip = tripNumber.trim()
      const get = (path: string) => reach(`${apiConfig.baseURL}/truck-loads${path}`, { headers: { Accept: 'application/json' }, cache: 'no-store' })

      let partner = partnerId
      if (partner === null) {
        const listed = await get('?direction=load')
        if (!listed.ok) throw new Error(`Trucks failed (${listed.status})`)
        const partners = partnersWithTrip(await listed.json(), trip)
        if (partners.length === 0) throw new LookupNotFoundError(`No truck at a door has trip ${trip}`)
        if (partners.length > 1) throw new LookupNotFoundError(`Trip ${trip} is on more than one partner's trucks — pick the truck from the orders list`)
        partner = partners[0]!
      }

      const response = await get(`/${partner}/${encodeURIComponent(trip)}?direction=load`)
      if (response.status === 404) throw new LookupNotFoundError(`No truck found for trip ${trip}`)
      if (!response.ok) throw new Error(`Truck lookup failed (${response.status})`)
      return mapTruckLoad(await response.json())
    },

    /**
     * The gangs the site knows, by name.
     *
     * `isActive` is deliberately NOT filtered on: every gang in live data has
     * it false, so honouring it would reject every gang an operator could
     * scan. If inactive gangs should be refused, filter here once the flag
     * means something.
     */
    async fetchGangs(): Promise<string[]> {
      // Narrowed to the warehouse this tablet is in: the read also tells the
      // tablet which warehouses there are (utils/warehouses.ts).
      const site = useWarehouse()
      site.noteGangs(await this.fetchGangEntries())
      return site.gangNames.value
    },

    /** Every gang the site has, with the warehouse each works in. */
    async fetchGangEntries(timeoutMs?: number): Promise<GangEntry[]> {
      const apiConfig = useApiConfig()
      const response = await reach(
        `${apiConfig.baseURL}/Gang`,
        { headers: { Accept: 'application/json' } },
        timeoutMs === undefined ? {} : { timeoutMs },
      )
      if (!response.ok) throw new Error(`Gang list failed (${response.status})`)
      return mapGangEntries(await response.json())
    },

    /** The pallets the SERVER records as loaded on an order. */
    async fetchLoadedPallets(partnerId: number, orderNumber: string): Promise<LoadedPalletInfo[]> {
      const apiConfig = useApiConfig()
      const response = await fetch(
        `${apiConfig.baseURL}/orders/${partnerId}/${encodeURIComponent(orderNumber)}/pallets-shipped`,
        { headers: { Accept: 'application/json' } },
      )
      if (!response.ok) throw new Error(`Loaded pallets failed (${response.status})`)
      return mapShippedPallets(await response.json())
    },

    /**
     * The trailer diagram for an order of exactly this many pallets, or null
     * when the office has none for a truck this size.
     *
     * The API answers with a list (task 2421): the one active layout of that
     * size, or none. A failed read throws -- the CALLER decides that a missing
     * diagram hides the panel rather than degrading the screen.
     */
    async fetchTrailerLayout(palletCount: number): Promise<TrailerLayoutInfo | null> {
      const apiConfig = useApiConfig()
      const response = await reach(
        `${apiConfig.baseURL}/trailer-layouts?palletCount=${palletCount}`,
        { headers: { Accept: 'application/json' }, cache: 'no-store' },
      )
      // An empty list is an answer, not a failure: the office has no active
      // layout for a truck this size. Null says that; a throw says the read did
      // not come back, which the screen shows differently and offers to retry.
      if (!response.ok) throw new Error(`Trailer layout failed (${response.status})`)
      const [layout] = await response.json() as TrailerLayoutDto[]
      return layout ? mapTrailerLayout(layout) : null
    },

    async fetchTrip(tripNumber: string, orderNumber?: string): Promise<TripInfo> {
      const apiConfig = useApiConfig()
      const query = encodeURIComponent(tripNumber.trim())
      const response = await fetch(
        `${apiConfig.baseURL}/orders/search?q=${query}&limit=50`,
        { headers: { Accept: 'application/json' } },
      )
      if (!response.ok) throw new Error(`Trip lookup failed (${response.status})`)
      // The endpoint matches CONTAINS across several fields; resolveTrip keeps
      // only exact trip matches.
      return resolveTrip(await response.json(), tripNumber, orderNumber)
    },
  },
})

async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json()
  } catch {
    return null
  }
}

/** What a problem body says: its detail first, as the API writes the sentence there. */
function problemText(body: unknown): string | null {
  const problem = body as { detail?: unknown, message?: unknown, title?: unknown } | null
  for (const text of [problem?.detail, problem?.message, problem?.title]) {
    if (typeof text === 'string' && text.trim()) return text.trim()
  }
  return null
}

async function readErrorMessage(response: Response): Promise<string | null> {
  try {
    const body = await response.json() as { message?: string, title?: string }
    return body.message ?? body.title ?? null
  } catch {
    return null
  }
}

